import { forwardRef } from "react";
import { Maximize2 } from "lucide-react";

/**
 * ProphetCard — square (1:1) card used inside the Meet the Prophets carousel.
 *
 * Data-driven: reads every field from the `prophet` object. Renders the real
 * NFT image (object-fit: cover) with a bottom name/family label bar that uses
 * the family accent for a restrained glow + thin accent line. If no image is
 * supplied, an elegant concentric-ring placeholder is shown as a fallback.
 *
 * Click behaviour:
 * - Side card (not active): `onSelect(idx)` centres this card.
 * - Active (centered) card:  `onSelect(idx)` opens the full-screen lightbox
 *   via the parent's handler. This is why the button remains clickable on
 *   the active card (previously it was disabled with `cursor: default`).
 * A discreet expand hint appears in the top-right corner on hover / focus
 * of the active card only — it never sits permanently over the artwork.
 */
export const ProphetCard = forwardRef(function ProphetCard(
  { prophet, offset, isActive, onSelect, index },
  ref,
) {
  const abs = Math.abs(offset);
  const translateX = offset * 74; // % of card width — good spread for square cards
  const scale      = abs === 0 ? 1 : abs === 1 ? 0.82 : abs === 2 ? 0.62 : 0.5;
  const rotateY    = offset === 0 ? 0 : -offset * 18;
  const opacity    = abs === 0 ? 1 : abs === 1 ? 0.85 : abs === 2 ? 0.45 : 0;
  const zIndex     = 10 - abs;
  const hidden     = abs > 2;
  const accent     = prophet.accent || "#c9d1e0";
  const accent2    = prophet.secondaryAccent || null;
  const accent3    = prophet.tertiaryAccent || null;

  const label = isActive
    ? `View ${prophet.name || `Prophet ${index + 1}`} artwork in full screen`
    : prophet.name
      ? `${prophet.name} — ${prophet.family}`
      : `Prophet card ${index + 1}`;

  return (
    <li
      aria-hidden={!isActive}
      data-testid={`prophet-card-${prophet.id}`}
      data-active={isActive || undefined}
      className={`prophets-card ${isActive ? "prophets-card--active" : ""}`}
      style={{
        transform: `translate(-50%, -50%) translateX(${translateX}%) scale(${scale}) rotateY(${rotateY}deg)`,
        opacity,
        zIndex,
        pointerEvents: hidden ? "none" : "auto",
        "--prophet-accent": accent,
        "--prophet-accent-soft": `${accent}33`,
        "--prophet-accent-2": accent2 || accent,
        "--prophet-accent-3": accent3 || accent,
      }}
    >
      <button
        ref={ref}
        type="button"
        onClick={onSelect}
        tabIndex={isActive ? 0 : -1}
        aria-label={label}
        data-testid={isActive ? "prophet-card-active-trigger" : undefined}
        className="prophets-card-inner group"
        style={{ cursor: isActive ? "zoom-in" : "pointer" }}
      >
        <span aria-hidden="true" className="prophets-card-corner prophets-card-corner--tl" />
        <span aria-hidden="true" className="prophets-card-corner prophets-card-corner--tr" />
        <span aria-hidden="true" className="prophets-card-corner prophets-card-corner--bl" />
        <span aria-hidden="true" className="prophets-card-corner prophets-card-corner--br" />

        {/* Discreet "expand" cue — only visible on hover/focus of active card
            (see CSS in prophets.css). Never covers the art permanently. */}
        {isActive && (
          <span
            aria-hidden="true"
            className="prophets-card-expand-hint"
            data-testid="prophet-card-expand-hint"
          >
            <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </span>
        )}

        <div className="prophets-card-media">
          {prophet.image ? (
            <img
              src={prophet.image}
              alt={prophet.alt || `${prophet.name || "Prophet"} of ${prophet.family || ""}`.trim()}
              className="prophets-card-image"
              draggable={false}
              loading={isActive ? "eager" : "lazy"}
            />
          ) : (
            <div className="prophets-card-placeholder" aria-hidden="true">
              <span className="prophets-card-placeholder-ring" />
              <span className="prophets-card-placeholder-ring prophets-card-placeholder-ring--inner" />
              <span className="p-mono prophets-card-placeholder-label">
                Prophet · 0{index + 1}
              </span>
            </div>
          )}

          {prophet.name && (
            <div className="prophets-card-label" aria-hidden={!isActive}>
              <span className="prophets-card-accent-bar" aria-hidden="true" />
              {accent2 && (
                <span className="prophets-card-accent-bar prophets-card-accent-bar--2" aria-hidden="true" />
              )}
              {accent3 && (
                <span className="prophets-card-accent-bar prophets-card-accent-bar--3" aria-hidden="true" />
              )}
              <span className="p-nav prophets-card-name">{prophet.name}</span>
              <span className="p-mono prophets-card-family">{prophet.family}</span>
            </div>
          )}
        </div>
      </button>
    </li>
  );
});
